// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { Resizable } from 're-resizable';
import '98.css';
import './App.css';

import tennaGif from '../src/assets/tennaDance.gif';

function App() {
  const [page, setPage] = useState('home');
  const [time, setTime] = useState(new Date());
  const [isMainOpen, setIsMainOpen] = useState(true);
  const [isClockOpen, setIsClockOpen] = useState(true);
  const [isMusicOpen, setIsMusicOpen] = useState(true);
  const [isTennaOpen, setIsTennaOpen] = useState(true);
  const [showAutoplayPrompt, setShowAutoplayPrompt] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [mainSize, setMainSize] = useState({ width: 400, height: 400 });
  const [clockSize, setClockSize] = useState({ width: 250, height: 100 });
  const [tennaSize, setTennaSize] = useState({ width: 150, height: 200 });
  const musicSize = { width: 300, height: 240 };

  // Position Anchors
  const mainAnchor = 'top-left';
  const clockAnchor = 'top-right';
  const musicAnchor = 'bottom-right';
  const tennaAnchor = 'bottom-left';

  // Initial Positions
  const getInitialPosition = (anchor, size) => {
    const { innerWidth, innerHeight } = window;
    switch (anchor) {
      case 'bottom-right':
        return { x: innerWidth - size.width - 20, y: innerHeight - size.height - 40 };
      case 'bottom-left':
        return { x: 20, y: innerHeight - size.height - 20 };
      case 'top-right':
        return { x: innerWidth - size.width - 20, y: 20 };
      case 'top-left':
      default:
        return { x: 20, y: 20 };
    }
  };

  const [mainPosition, setMainPosition] = useState(() => getInitialPosition(mainAnchor, mainSize));
  const [clockPosition, setClockPosition] = useState(() => getInitialPosition(clockAnchor, clockSize));
  const [musicPosition, setMusicPosition] = useState(() => getInitialPosition(musicAnchor, musicSize));
  const [tennaPosition, setTennaPosition] = useState(() => getInitialPosition(tennaAnchor, tennaSize));

  const mainWindowRef = useRef(null);
  const clockWindowRef = useRef(null);
  const musicWindowRef = useRef(null);
  const audioRef = useRef(null);
  const tennaWindowRef = useRef(null);

  const playlist = [
    { title: 'Balatro Main Theme', file: '/songs/Balatro_Main_Theme.mp3' },
    { title: 'Sheep Raider - Track 7', file: '/songs/Sheep_Raider_Track_7.mp3' },
    { title: 'DKC - Mangrove Cove', file: '/songs/TropicalFreeze_Mangrove_Cove.mp3' },
    { title: 'Tunic - Ocean Glaze', file: '/songs/TUNIC_Ocean_Glaze.mp3' },
    { title: 'A Hat in Time - Scootin Through Clocktowers', file: '/songs/HatInTime_ScootinThroughClocktowersBeneathTheSea.mp3' },
  ];

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (audioRef.current) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => setShowAutoplayPrompt(true));
        }
      }
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const handleUserAutoplay = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setShowAutoplayPrompt(false);
    }
  };

  const handleMouseDown = (e, setPosition) => {
    e.preventDefault();
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const handleMouseMove = (moveEvent) => {
      setPosition({
        x: moveEvent.clientX - offsetX,
        y: moveEvent.clientY - offsetY,
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleSongEnd = () => {
    setCurrentTrackIndex((prevIndex) => (prevIndex + 1) % playlist.length);
  };

  const mainScale = mainSize.width / 400;
  const clockScale = clockSize.width / 250;

  // Re-position anchored windows on resize
  useEffect(() => {
    const updateAnchoredPositions = () => {
      if (musicAnchor === 'bottom-right') {
        setMusicPosition({
          x: window.innerWidth - musicSize.width - 20,
          y: window.innerHeight - musicSize.height - 40,
        });
      }
      if (clockAnchor === 'top-right') {
        setClockPosition({
          x: window.innerWidth - clockSize.width - 20,
          y: 20,
        });
      }
    };

    window.addEventListener('resize', updateAnchoredPositions);
    return () => window.removeEventListener('resize', updateAnchoredPositions);
  }, [clockSize]);

  return (
    <div className="desktop">
      {showAutoplayPrompt && (
        <div className="autoplay-overlay">
          <div className="window" style={{ width: '300px', margin: 'auto' }}>
            <div className="title-bar">
              <div className="title-bar-text">Enable Audio</div>
            </div>
            <div className="window-body">
              <p>This site wants to autoplay music. Click below to enable:</p>
              <button onClick={handleUserAutoplay}>Enable Music</button>
            </div>
          </div>
        </div>
      )}

      {/* MAIN WINDOW */}
      <Resizable
        lockAspectRatio
        size={mainSize}
        minWidth={400}
        minHeight={400}
        onResize={(e, direction, ref, d) => {
          setMainSize({ width: ref.offsetWidth, height: ref.offsetHeight });
        }}
        enable={{ bottom: true, right: true, bottomRight: true }}
        style={{
          position: 'absolute',
          left: `${mainPosition.x}px`,
          top: `${mainPosition.y}px`,
          display: isMainOpen ? 'block' : 'none',
        }}
      >
        <div ref={mainWindowRef} className="window draggable" style={{ width: '100%', height: '100%' }}>
          <div className="title-bar" onMouseDown={(e) => handleMouseDown(e, setMainPosition)} style={{ cursor: 'move' }}>
            <div className="title-bar-text">Homepage</div>
            <div className="title-bar-controls">
              <button aria-label="Close" onClick={() => setIsMainOpen(false)} />
            </div>
          </div>
          <div className="window-body" style={{ fontSize: `${Math.max(12, 24 * mainScale)}px`, overflow: 'auto' }}>
            <div className="tab-bar" style={{ display: 'flex', gap: `${8 * mainScale}px`, marginBottom: `${12 * mainScale}px` }}>
              {['home', 'about', 'blog'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setPage(tab)}
                  style={{
                    fontSize: `${16 * mainScale}px`,
                    padding: `${4 * mainScale}px ${12 * mainScale}px`,
                  }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
            <div className="page-content">
              {page === 'home' && <p>Welcome!</p>}
              {page === 'about' && <p>About: Cheezin.</p>}
              {page === 'blog' && <p>Blog: Coming soon...</p>}
            </div>
          </div>
        </div>
      </Resizable>

      {/* CLOCK WINDOW */}
      <Resizable
        lockAspectRatio
        size={clockSize}
        minWidth={150}
        minHeight={100}
        onResize={(e, direction, ref, d) => {
          setClockSize({ width: ref.offsetWidth, height: ref.offsetHeight });
        }}
        enable={{ bottom: true, right: true, bottomRight: true }}
        style={{
          position: 'absolute',
          left: `${clockPosition.x}px`,
          top: `${clockPosition.y}px`,
          display: isClockOpen ? 'block' : 'none',
        }}
      >
        <div ref={clockWindowRef} className="window draggable" style={{ width: '100%', height: '100%' }}>
          <div className="title-bar" onMouseDown={(e) => handleMouseDown(e, setClockPosition)} style={{ cursor: 'move' }}>
            <div className="title-bar-text">Clock</div>
            <div className="title-bar-controls">
              <button aria-label="Close" onClick={() => setIsClockOpen(false)} />
            </div>
          </div>
          <div className="window-body clock-body" style={{ fontSize: `${Math.max(24, 40 * clockScale)}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, height: '100%' }}>
            {time.toLocaleTimeString()}
          </div>
        </div>
      </Resizable>

      {/* MUSIC PLAYER WINDOW */}
      <div
        ref={musicWindowRef}
        className="window draggable"
        style={{
          position: 'absolute',
          top: `${musicPosition.y}px`,
          left: `${musicPosition.x}px`,
          width: `${musicSize.width}px`,
          height: `${musicSize.height}px`,
          zIndex: isMusicOpen ? 2 : -1,
          visibility: isMusicOpen ? 'visible' : 'hidden',
        }}
      >
        <div className="title-bar" onMouseDown={(e) => handleMouseDown(e, setMusicPosition)} style={{ cursor: 'move' }}>
          <div className="title-bar-text">WinAmp</div>
          <div className="title-bar-controls">
            <button aria-label="Close" onClick={() => setIsMusicOpen(false)} />
          </div>
        </div>
        <div className="window-body" style={{ height: 'calc(100% - 25px)', overflow: 'hidden' }}>
          <strong>Now Playing:</strong>
          <p>{playlist[currentTrackIndex].title}</p>
          <audio
            ref={audioRef}
            src={playlist[currentTrackIndex].file}
            controls
            autoPlay
            onEnded={handleSongEnd}
            style={{ width: '100%' }}
          />
          <hr />
          <strong>Playlist:</strong>
          <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
            <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
              {playlist.map((song, index) => (
                <li key={index}>
                  <button
                    style={{ width: '100%', marginBottom: '4px' }}
                    onClick={() => {
                      setCurrentTrackIndex(index);
                      setTimeout(() => audioRef.current.play(), 100);
                    }}
                  >
                    {song.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* TENNA GIF WINDOW */}
      <Resizable
        lockAspectRatio={true}
        size={tennaSize}
        minWidth={100}
        minHeight={100}
        onResize={(e, direction, ref, d) => {
          setTennaSize({ width: ref.offsetWidth, height: ref.offsetHeight });
        }}
        enable={{ bottom: true, right: true, bottomRight: true }}
        style={{
          position: 'absolute',
          left: `${tennaPosition.x}px`,
          top: `${tennaPosition.y}px`,
          display: isTennaOpen ? 'block' : 'none',
        }}
      >
        <div className="window draggable" style={{ width: '100%', height: '100%' }}>
          <div
            className="title-bar"
            onMouseDown={(e) => handleMouseDown(e, setTennaPosition)}
            style={{ cursor: 'move' }}
          >
            <div className="title-bar-text">Tenna</div>
            <div className="title-bar-controls">
              <button aria-label="Close" onClick={() => setIsTennaOpen(false)} />
            </div>
          </div>
          <div
            className="window-body"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              height: '100%',
            }}
          >
            <img src={tennaGif} alt="Dancing Tenna" style={{ maxWidth: '100%', maxHeight: '100%' }} />
          </div>
        </div>
      </Resizable>

      {/* TASKBAR */}
      <div className="taskbar">
        {!isMainOpen && <button onClick={() => setIsMainOpen(true)}>Homepage</button>}
        {!isClockOpen && <button onClick={() => setIsClockOpen(true)}>Clock</button>}
        {!isMusicOpen && <button onClick={() => setIsMusicOpen(true)}>WinAmp</button>}
        {!isTennaOpen && <button onClick={() => setIsTennaOpen(true)}>Tenna</button>}
      </div>
    </div>
    
  );
}

export default App;
